import { useState } from 'react';
import { AdnReport } from '../../types/adnReport';
import { format, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Bar, Legend } from 'recharts';
import { useWindowSize } from '../../hooks/useWindowSize';

interface AdnTrendChartProps {
  reports: AdnReport[];
}

const CATEGORIES = [
  { id: 'visitors', title: 'Évolution des nouveaux visiteurs' },
  { id: 'gender', title: 'Nouveaux visiteurs par genre' },
  { id: 'decisions', title: 'Évolution des décisions' }
] as const;

export function AdnTrendChart({ reports }: AdnTrendChartProps) {
  const [selectedCategory, setSelectedCategory] = useState<typeof CATEGORIES[number]['id']>('visitors');
  const { width } = useWindowSize();
  const isMobile = width < 768;

  // Filter reports from last 3 months and sort by date
  const threeMonthsAgo = subMonths(new Date(), 3);
  const filteredReports = reports
    .filter(report => new Date(report.date) >= threeMonthsAgo)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Prepare chart data
  const chartData = filteredReports.map(report => ({
    date: format(new Date(report.date), isMobile ? 'dd/MM' : 'dd/MM/yy', { locale: fr }),
    totalVisitors: report.totalNewVisitors,
    men: report.newVisitors.men,
    women: report.newVisitors.women,
    undecided: report.visitorDecisions.undecided,
    wantsToJoin: report.totalWantsToJoin,
    wantsToGiveLifeToJesus: report.totalWantsToGiveLifeToJesus
  }));

  const renderChart = () => {
    if (selectedCategory === 'visitors') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={chartData} 
            margin={{ 
              top: 5, 
              right: isMobile ? 10 : 30, 
              left: isMobile ? 0 : 20, 
              bottom: isMobile ? 40 : 25 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
              width={isMobile ? 30 : 40}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: isMobile ? 12 : 14
              }}
            />
            <Line
              type="monotone"
              dataKey="totalVisitors"
              stroke="#8B5CF6"
              strokeWidth={2}
              dot={{ fill: '#8B5CF6', r: isMobile ? 3 : 4 }}
              name="Total nouveaux visiteurs"
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (selectedCategory === 'gender') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart 
            data={chartData} 
            margin={{ 
              top: 5, 
              right: isMobile ? 10 : 30, 
              left: isMobile ? 0 : 20, 
              bottom: isMobile ? 40 : 25 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
              width={isMobile ? 30 : 40}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: isMobile ? 12 : 14
              }}
            />
            <Legend 
              verticalAlign="top" 
              height={36}
              wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
            />
            <Bar 
              dataKey="men" 
              fill="#8B5CF6" 
              name="Hommes"
              barSize={isMobile ? 15 : 20}
            />
            <Bar 
              dataKey="women" 
              fill="#F59E0B" 
              name="Femmes"
              barSize={isMobile ? 15 : 20}
            />
            <Line
              type="monotone"
              dataKey="totalVisitors"
              stroke="#DC2626"
              strokeWidth={2}
              dot={{ fill: '#DC2626', r: isMobile ? 3 : 4 }}
              name="Total"
            />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    if (selectedCategory === 'decisions') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart 
            data={chartData} 
            margin={{ 
              top: 5, 
              right: isMobile ? 10 : 30, 
              left: isMobile ? 0 : 20, 
              bottom: isMobile ? 40 : 25 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fill: '#4B5563', fontSize: isMobile ? 10 : 12 }}
              width={isMobile ? 30 : 40}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                fontSize: isMobile ? 12 : 14
              }}
            />
            <Legend 
              verticalAlign="top" 
              height={36}
              wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
            />
            <Bar 
              dataKey="undecided" 
              fill="#F59E0B" 
              name="Indécis"
              barSize={isMobile ? 15 : 20}
            />
            <Bar 
              dataKey="wantsToJoin" 
              fill="#10B981" 
              name="Veulent rejoindre"
              barSize={isMobile ? 15 : 20}
            />
            <Bar 
              dataKey="wantsToGiveLifeToJesus" 
              fill="#8B5CF6" 
              name="Vie à Jésus"
              barSize={isMobile ? 15 : 20}
            />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Courbes de tendances ADN</h3>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 -mx-3 px-3 md:mx-0 md:px-0">
          <div className="flex gap-2 min-w-max">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-2 py-1.5 md:px-3 md:py-2 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-[#00665C] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category.title}
              </button>
            ))}
          </div>
        </div>
        <div className="h-[250px] md:h-[350px] w-full">
          {chartData.length > 0 ? (
            renderChart()
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <p>Aucune donnée disponible pour les graphiques</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}